<template>
  <ApplicationsLane
    v-if="applications"
    :title="title"
    :applications="applications"
    :container-style="containerStyle"
    :status="status"
  >
    <template #controls>
      <SelectWrapper v-if="status === 'waiting'">
        <select v-model="activeSorting">
          <option value="sentToHostAt-NEW">Nyaste ärendet</option>
          <option value="sentToHostAt-OLD">Äldsta ärendet</option>
        </select>
      </SelectWrapper>
      <SelectWrapper v-else hidden>
        <select disabled></select>
      </SelectWrapper>
    </template>
    <template #body>
      <transition-group
        v-for="(groupedApplication, date, index) in groupedApplications"
        :key="date"
        name="fade-list"
        class="relative"
        :class="{ 'card-group': status === 'closed' }"
        tag="div"
        appear
      >
        <div v-if="status === 'approved'" :key="index" class="app-date">
          {{ getGroupText(date) }}
        </div>
        <OvApplicationCard
          v-for="application in groupedApplication"
          :key="application.id"
          :application="application"
          :reason-type-title="ovReasonText(application.reason)"
          :is-new="cardIsNew(application)"
          :full-name="fullName(application)"
          :last-name="lastName(application)"
          :status-date="
            getStatusDate(
              status,
              application.statusUpdated,
              application.sentToHostAt
            )
          "
          :to-path="toPath"
        />
      </transition-group>
    </template>
  </ApplicationsLane>
</template>

<script>
import statusTypes from '@/utils/statusTypes';
import { ovReasonText } from '@/utils/ovUtils';

import ApplicationsLane from '@/components/ApplicationsLane.vue';
import OvApplicationCard from '@/components/OvApplicationCard.vue';
import SelectWrapper from '@/components/SelectWrapper.vue';

export default {
  name: 'OvApplicationsLane',
  components: {
    OvApplicationCard,
    ApplicationsLane,
    SelectWrapper
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    containerStyle: {
      type: Object,
      default: null
    },
    applications: {
      type: [Array, null],
      default: null
    },
    toPath: {
      type: String,
      default: '/overlatelser-privat/alla'
    }
  },
  data() {
    return {
      ovReasonText,
      activeSorting: 'sentToHostAt-NEW'
    };
  },
  computed: {
    closedStatuses() {
      return [
        statusTypes.CLOSED,
        statusTypes.REJECTED,
        statusTypes.APPROVED,
        statusTypes.ABORTED
      ];
    },
    groupedApplications() {
      if (!this.applications) {
        return {};
      }
      const groups = {};
      const newApplications = [...this.applications];
      if (this.status !== 'waiting') {
        // Alla arkivkolumner (closed/rejected/removed): nyaste överst efter statusUpdated
        if (this.status !== 'approved') {
          const sorted = newApplications.sort((a, b) => {
            const aDate = new Date(a.statusUpdated || 0).getTime();
            const bDate = new Date(b.statusUpdated || 0).getTime();
            return bDate - aDate;
          });
          return { 0: sorted };
        }

        newApplications.sort((a, b) => {
          const aDate = new Date(a.actualTransferDate);
          const bDate = new Date(b.actualTransferDate);
          return aDate - bDate;
        });

        newApplications.forEach(function (application) {
          let date = application.actualTransferDate
            ? new Date(application.actualTransferDate)
                .toLocaleDateString('sv-SE', {
                  year: 'numeric',
                  month: '2-digit'
                })
                .slice(0, 7)
            : '-';
          if (date in groups) {
            groups[date].push(application);
          } else {
            groups[date] = new Array(application);
          }
        });

        return groups;
      } else {
        return {
          0: newApplications.sort((a, b) => {
            if (this.activeSorting.includes('sentToHostAt')) {
              const aSentToHost = new Date(a.sentToHostAt).getTime();
              const bSentToHost = new Date(b.sentToHostAt).getTime();
              if (this.activeSorting.includes('NEW')) {
                return bSentToHost - aSentToHost;
              }
              return aSentToHost - bSentToHost;
            }
            return true;
          })
        };
      }
    }
  },
  methods: {
    getStatusDate(status, statusUpdated, sentToHostAt) {
      const statusFormatted = status ? status.toUpperCase() : '';
      if (this.closedStatuses.includes(statusFormatted)) {
        return statusUpdated ? statusUpdated.substring(0, 10) : '';
      }
      if (statusFormatted === 'WAITING' && statusUpdated) {
        return statusUpdated.substring(0, 10);
      }
      return sentToHostAt ? sentToHostAt.substring(0, 10) : '';
    },
    getMonthText(month) {
      switch (month) {
        case '01':
          return 'Januari';
        case '02':
          return 'Februari';
        case '03':
          return 'Mars';
        case '04':
          return 'April';
        case '05':
          return 'Maj';
        case '06':
          return 'Juni';
        case '07':
          return 'Juli';
        case '08':
          return 'Augusti';
        case '09':
          return 'September';
        case '10':
          return 'Oktober';
        case '11':
          return 'November';
        case '12':
          return 'December';
        default:
          return '';
      }
    },
    getGroupText(monthDate) {
      const month = monthDate.slice(5, 7);
      const fullYear = monthDate.slice(0, 4);
      const startText = 'Överlåtelser i';
      return `${startText} ${this.getMonthText(month)} (${fullYear})`;
    },
    fullName(application) {
      return (
        application.assignedManagerFirstName ||
        application.assignedLandlordFirstName ||
        ''
      );
    },
    lastName(application) {
      return (
        application.assignedManagerLastName ||
        application.assignedLandlordLastName ||
        ''
      );
    },
    cardIsNew(application) {
      return application.status === statusTypes.READY && application.isNew;
    }
  }
};
</script>

<style scoped>
.app-date {
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  background-color: #fff;
  padding: 10px;
  margin-bottom: 0.3rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 5%);
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.card-group > *:nth-last-child(1) {
  margin-bottom: 3rem;
}
</style>
